import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { PAGE_SIZES, PLAN_STATE_STATUSES } from '@app/shared';
import type { PlanDto, PlanStateStatus } from '@app/shared';
import { tokens } from '../../app/theme';
import { FilterPanel } from '../../components/FilterPanel';
import { useNotify } from '../../components/NotifyProvider';
import { EMPTY_TEXT, ErrorState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { ConfirmDialog } from '../../components/dialogs';
import { formatCents } from '../../lib/format';
import { useListState } from '../../lib/useListState';
import { PlanNameDialog } from './PlanNameDialog';
import { plansExportUrl, usePlans, useSetPlanStateStatus } from './api';
import { useCarriers, usePlanOptions } from './lookups';
import { STATE_OPTIONS, carrierOptions } from './options';

/** Parent-row columns in the reference's order. `sort` is the field sent to the API. */
const PARENT_COLUMNS: Array<{ header: string; sort?: string; align?: 'right' }> = [
  { header: 'Action' },
  { header: 'Plan Name', sort: 'name' },
  { header: 'Plan Code', sort: 'code' },
  { header: 'Carrier Details' },
  { header: 'Plan Price', sort: 'priceCents', align: 'right' },
  { header: 'Total State Configured', align: 'right' },
  { header: 'Plan Type', sort: 'type' },
];
const CHILD_COLUMNS = [
  'Action',
  'Plan Name',
  'Plan Code',
  'Carrier',
  'Plan Price',
  'State',
  'Status',
  'Plan Type',
];

const other = (status: PlanStateStatus): PlanStateStatus => (status === 'LIVE' ? 'DEMO' : 'LIVE');

interface StatusChange {
  plan: PlanDto;
  state: string;
  status: PlanStateStatus;
}

/** "Plan" tab of Plan Config: one row per plan, expandable to one row per state. */
export function PlansTab() {
  const notify = useNotify();
  const list = useListState(
    { name: '', state: '', carrierId: '', code: '', status: '', display: '' },
    10,
    'name',
  );
  const plans = usePlans(list.params);
  const carriers = useCarriers();
  const planOptions = usePlanOptions();
  const setStateStatus = useSetPlanStateStatus();

  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [renaming, setRenaming] = useState<PlanDto | null>(null);
  const [statusChange, setStatusChange] = useState<StatusChange | null>(null);

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const confirmStatus = async () => {
    if (!statusChange) return;
    const { plan, state, status } = statusChange;
    try {
      await setStateStatus.mutateAsync({ id: plan.id, state, status });
      notify.success(`${plan.name} is now ${status} in ${state}.`);
    } catch (err) {
      notify.error(err);
    } finally {
      setStatusChange(null);
    }
  };

  const sortField = list.sort?.replace(/^-/, '');
  const sortDir: 'asc' | 'desc' = list.sort?.startsWith('-') ? 'desc' : 'asc';
  const toggleSort = (field: string) =>
    list.setSort(sortField === field && sortDir === 'asc' ? `-${field}` : field);

  const rows = plans.data?.data;
  const loading = plans.isFetching;
  const codes = [...(planOptions.data ?? [])].sort((a, b) => a.code - b.code);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          Prepaid Plans
        </Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          component="a"
          href={plansExportUrl({ ...list.applied, sort: list.sort })}
          download
        >
          Download
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/settings/plans/new"
        >
          Add Plan
        </Button>
      </Box>

      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          label="Plan name"
          value={list.draft.name}
          onChange={(e) => list.setField('name', e.target.value)}
        />
        <TextField
          select
          label="State"
          value={list.draft.state}
          onChange={(e) => list.setField('state', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {STATE_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Carrier"
          value={list.draft.carrierId}
          onChange={(e) => list.setField('carrierId', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {carrierOptions(carriers.data).map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Plan Code"
          value={list.draft.code}
          onChange={(e) => list.setField('code', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {codes.map((p) => (
            <MenuItem key={p.id} value={String(p.code)}>
              {p.code}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={list.draft.status}
          onChange={(e) => list.setField('status', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {PLAN_STATE_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Is Display"
          value={list.draft.display}
          onChange={(e) => list.setField('display', e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="false">No</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
        </TextField>
      </FilterPanel>

      {/* Same look and paging as components/DataTable, which has no expandable rows. */}
      {plans.error ? (
        <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />
      ) : (
        <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
          <Typography sx={{ px: 1.5, py: 0.75, fontWeight: 600 }}>
            Total record: {plans.data?.total ?? 0}
          </Typography>
          <Box sx={{ height: 4 }}>{loading ? <LinearProgress aria-label="Loading" /> : null}</Box>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
            <Table stickyHeader aria-label="Plans">
              <TableHead>
                <TableRow>
                  {PARENT_COLUMNS.map((c) => (
                    <TableCell key={c.header} align={c.align}>
                      {c.sort ? (
                        <TableSortLabel
                          active={sortField === c.sort}
                          direction={sortField === c.sort ? sortDir : 'asc'}
                          onClick={() => toggleSort(c.sort!)}
                        >
                          {c.header}
                        </TableSortLabel>
                      ) : (
                        c.header
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.map((plan) => {
                  const expanded = open.has(plan.id);
                  return (
                    <Fragment key={plan.id}>
                      <TableRow hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => toggle(plan.id)}
                            aria-expanded={expanded}
                            aria-label={`${expanded ? 'Hide' : 'Show'} states of ${plan.name}`}
                          >
                            {expanded ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Button
                            component={Link}
                            to={`/settings/plans/${plan.id}/edit`}
                            aria-label={`Edit ${plan.name}`}
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => setRenaming(plan)}
                            aria-label={`Edit Plan Name of ${plan.name}`}
                          >
                            Edit Plan Name
                          </Button>
                        </TableCell>
                        <TableCell>{plan.name}</TableCell>
                        <TableCell>{plan.code}</TableCell>
                        <TableCell>
                          {[plan.carrierCode, ...plan.moreCarrierCodes]
                            .map((c) => `${c.carrier.code} / ${c.code}`)
                            .join(', ')}
                        </TableCell>
                        <TableCell align="right">{formatCents(plan.priceCents)}</TableCell>
                        <TableCell align="right">{plan.states.length}</TableCell>
                        <TableCell>{plan.type}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={PARENT_COLUMNS.length}
                          sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}
                        >
                          <Collapse in={expanded} unmountOnExit>
                            <Box sx={{ px: 2, py: 1 }}>
                              <Table aria-label={`States of ${plan.name}`}>
                                <TableHead>
                                  <TableRow>
                                    {CHILD_COLUMNS.map((header) => (
                                      // Lighter than the parent header so the two levels read apart.
                                      <TableCell
                                        key={header}
                                        sx={{ bgcolor: tokens.cardHeader, color: tokens.cardTitle }}
                                      >
                                        {header}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {plan.states.map((s) => (
                                    <TableRow key={s.state}>
                                      <TableCell>
                                        {/* Assumption: the reference's per-state action was not observed; it switches LIVE/DEMO here. */}
                                        <Button
                                          onClick={() =>
                                            setStatusChange({
                                              plan,
                                              state: s.state,
                                              status: other(s.status),
                                            })
                                          }
                                          aria-label={`Set ${plan.name} ${other(s.status)} in ${s.state}`}
                                        >
                                          Set {other(s.status)}
                                        </Button>
                                      </TableCell>
                                      <TableCell>{plan.name}</TableCell>
                                      <TableCell>{plan.code}</TableCell>
                                      <TableCell>
                                        {[plan.carrierCode, ...plan.moreCarrierCodes]
                                          .map((c) => c.carrier.code)
                                          .join(', ')}
                                      </TableCell>
                                      <TableCell>{formatCents(plan.priceCents)}</TableCell>
                                      <TableCell>{s.state}</TableCell>
                                      <TableCell>
                                        <StatusChip status={s.status} />
                                      </TableCell>
                                      <TableCell>{plan.type}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
                {rows && rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={PARENT_COLUMNS.length} align="center" sx={{ py: 3 }}>
                      {EMPTY_TEXT}
                    </TableCell>
                  </TableRow>
                ) : null}
                {!rows && loading ? (
                  <TableRow>
                    <TableCell colSpan={PARENT_COLUMNS.length} align="center" sx={{ py: 3 }}>
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={plans.data?.total ?? 0}
            page={Math.max(0, list.page - 1)}
            rowsPerPage={list.pageSize}
            rowsPerPageOptions={[...PAGE_SIZES]}
            labelRowsPerPage="Record limit:"
            onPageChange={(_e, p) => list.setPage(p + 1)}
            onRowsPerPageChange={(e) => list.setPageSize(Number(e.target.value))}
            showFirstButton
            showLastButton
          />
        </Paper>
      )}

      <PlanNameDialog plan={renaming} onClose={() => setRenaming(null)} />

      <ConfirmDialog
        open={Boolean(statusChange)}
        title="Change plan status"
        message={
          statusChange
            ? `Set "${statusChange.plan.name}" to ${statusChange.status} in ${statusChange.state}?`
            : ''
        }
        confirmLabel={statusChange ? `Set ${statusChange.status}` : 'Confirm'}
        busy={setStateStatus.isPending}
        onConfirm={() => void confirmStatus()}
        onClose={() => setStatusChange(null)}
      />
    </Box>
  );
}
